import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TenantService } from '../../core/tenant.service';
import { DEFAULT_FEATURES, resolveFeatures } from '../../core/feature-registry';
import { NAV_ICON_PATHS } from './nav-icons';

/**
 * Sidebar navigation component for the Tenant Portal.
 *
 * Derives navigation items from the Feature Configuration Registry based on
 * the current tenant's business_type and active_modules.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 6.5
 */
@Component({
    selector: 'app-sidebar-nav',
    imports: [RouterLink, RouterLinkActive, TranslateModule],
    styles: [],
    template: `
    <div class="px-4 pt-6 pb-4 border-b border-white/10 flex flex-col gap-1">
      <div class="text-lg font-bold text-white">{{ 'common.tenantPortal' | translate }}</div>
      <button
        class="hidden max-md:inline-flex items-center justify-center self-end p-2 text-white
               focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2 rounded"
        type="button"
        [attr.aria-label]="(isOpen ? 'common.closeNavMenu' : 'common.openNavMenu') | translate"
        [attr.aria-expanded]="isOpen"
        aria-controls="sidebar-nav-list"
        (click)="toggleHamburger()"
      >{{ isOpen ? '✕' : '☰' }}</button>
    </div>

    <nav [attr.aria-label]="'nav.mainNavigation' | translate">
      <ul id="sidebar-nav-list" class="py-2">
        @for (item of navItems(); track item.route) {
          <li>
            <a
              [routerLink]="item.route"
              routerLinkActive="!bg-blue-600 !text-white border-l-[3px] border-l-blue-400"
              [routerLinkActiveOptions]="{ exact: isExactRoute(item.route) }"
              #rla="routerLinkActive"
              [attr.aria-current]="rla.isActive ? 'page' : null"
              class="flex items-center gap-3 px-4 py-3 text-sm text-slate-100 no-underline transition-colors duration-150
                     hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-[-2px]"
            >
              @if (iconFor(item.icon); as icon) {
                <svg
                  viewBox="0 0 24 24"
                  class="w-5 h-5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  [innerHTML]="icon"
                ></svg>
              }
              <span>{{ item.labelKey | translate }}</span>
            </a>
          </li>
        }
      </ul>
    </nav>
  `
})
export class SidebarNavComponent {
  @Input() isOpen = false;
  @Output() hamburgerToggle = new EventEmitter<void>();

  private readonly tenantService = inject(TenantService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly tenant = toSignal(this.tenantService.tenant$, { initialValue: null });

  /** Sanitized SVG inner markup cache keyed by icon id (markup is a fixed, trusted constant). */
  private readonly iconCache = new Map<string, SafeHtml>();

  readonly navItems = computed(() => {
    const tenant = this.tenant();
    if (!tenant) {
      return DEFAULT_FEATURES;
    }
    return resolveFeatures(
      tenant.business_type,
      tenant.active_modules ?? [],
      {
        quotations_enabled: tenant.quotations_enabled ?? true,
        work_orders_enabled: tenant.work_orders_enabled ?? true,
        invoicing_enabled: tenant.invoicing_enabled ?? true,
      },
      tenant.billing_tier
    )
      // Audit is reached from Settings → System Settings → Audit (available on
      // all plans), NOT as a top-level sidebar item. It stays in the feature
      // registry so the route guard still permits it.
      .filter((f) => f.route !== '/audit');
  });

  toggleHamburger(): void {
    this.hamburgerToggle.emit();
  }

  /**
   * Returns the sanitized SVG inner markup for a nav item's icon key, or null
   * when the item has no icon. The markup comes from the fixed NAV_ICON_PATHS
   * constant (not user input), so bypassing sanitization is safe; results are
   * cached so each icon is only trusted once.
   */
  iconFor(key: string | undefined): SafeHtml | null {
    if (!key) {
      return null;
    }
    const cached = this.iconCache.get(key);
    if (cached) {
      return cached;
    }
    const markup = NAV_ICON_PATHS[key];
    if (!markup) {
      return null;
    }
    const safe = this.sanitizer.bypassSecurityTrustHtml(markup);
    this.iconCache.set(key, safe);
    return safe;
  }

  /**
   * Returns true if this route should use exact matching for routerLinkActive.
   * Routes that are prefixes of other registered routes need exact matching
   * to avoid both parent and child being highlighted (e.g., /clients vs /clients/tickets).
   */
  isExactRoute(route: string): boolean {
    return this.navItems().some(
      item => item.route !== route && item.route.startsWith(route + '/')
    );
  }
}
